import { useEffect, useState } from 'react';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';
import { NudosSelectButton } from 'components/NudosComponents';
import { getProfileType } from 'services/cataloge';
import { IProfileType } from 'types/forms';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './ProfileDropdown.scss';

interface IProfileDropdown {
  value: number[];
  onChange: (profileIds: number[]) => void;
}

const MAX_PROFILES = 4;

/**
 * Selector múltiple de perfiles (máximo 4) para la creación de una herramienta desde cotización.
 * Carga los perfiles del catálogo al montar y permite seleccionar/deseleccionar perfiles.
 * Controlado por el padre a través de `value` y `onChange`.
 */
const ProfileDropdown = ({ value, onChange }: IProfileDropdown) => {
  const [profiles, setProfiles] = useState<IProfileType>([]);
  const [isLoading, setIsLoading] = useState(false);

  const profileIdToName = profiles.reduce<{ [id: string]: string }>(
    (mapping, profile) => ({ ...mapping, [`${profile.id}`]: profile.name }),
    {}
  );
  const selectedValue = value.length
    ? value.map(profileId => profileIdToName[`${profileId}`]).join(', ')
    : 'Selecciona el perfil';

  /** Agrega el perfil si no está seleccionado y hay cupo, o lo quita si ya estaba. */
  const handleToggleProfile = (profileId: number) => {
    if (!profileId) return;
    const isSelected = value.includes(profileId);
    if (isSelected) {
      onChange(value.filter(id => id !== profileId));
    } else if (value.length < MAX_PROFILES) {
      onChange([...value, profileId]);
    }
  };

  /** Construye las opciones del dropdown como botones de selección. */
  const buildOptions = (): JSX.Element[] => {
    return profiles.map(profile => (
      <div key={profile.id} className="profileOption" onClick={() => handleToggleProfile(profile.id)}>
        <NudosSelectButton isButtonActive={value.includes(profile.id)} colorPalette="black" />
        <div className="optionText">{profile.name}</div>
      </div>
    ));
  };

  useEffect(() => {
    setIsLoading(true);
    getProfileType()
      .then(data => setProfiles(data))
      .catch(() => displayErrorNotification())
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="profileDropdown">
      <BordSelectDropdown
        label="Perfil (Máximo 4)*"
        selectOptions={buildOptions()}
        currentValueOrplaceholder={selectedValue}
        isFilled={value.length > 0}
        closesOnChangeSelection={false}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ProfileDropdown;
